// admin-analytics-expansion P2b (tasks 2b.3, design D4/D5, spec #2559 R6) —
// TicketBrowsePage
//
// Cursor-paginated, filterable ticket list. Reuses `useCursorPagination`
// (the shared keyset-pagination hook, JQ4-45) wired to `browseTickets()`.
// Role scoping is entirely server-enforced (assertLocationScope +
// supervisor auto-scope, P2a); this page is a thin UX shell mirroring
// AuditLogPage/AnalyticsPage's conventions:
//   - `getMyLocations()` supplies BOTH the location switcher options AND the
//     client-side locationId -> name join for the table (D3 pattern, reused
//     from LocationBreakdownTable) — a supervisor's response is already
//     restricted to their assigned locations server-side, so the switcher
//     never needs its own role-branching.
//   - R6 "filter change resets pagination" needs NO explicit reset code:
//     `useCursorPagination`'s `queryKey` includes every filter value, so
//     TanStack Query discards prior pages and starts a fresh page 1 the
//     instant any filter changes.

import { browseTickets, getMyLocations } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { TicketBrowseQuery } from '@jq4race/shared';
import { Button } from '@jq4race/ui';
import { useQuery } from '@tanstack/react-query';
import { Download, RefreshCw, TriangleAlert, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useCursorPagination } from '../../../../shared/hooks/useCursorPagination';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { TicketBrowseTable } from '../components/TicketBrowseTable';
import {
  INITIAL_TICKET_FILTERS,
  TicketFilterBar,
  type TicketFilters,
} from '../components/TicketFilterBar';
import { type TicketExportState, useTicketExport } from '../hooks/useTicketExport';

const PAGE_LIMIT = 25;

export function TicketBrowsePage() {
  const { t } = useTranslation('tickets');
  const [filters, setFilters] = useState<TicketFilters>(INITIAL_TICKET_FILTERS);
  const { state: exportState, startExport } = useTicketExport();

  // Role-scoped location list — also doubles as the id -> name join for the
  // table (D3). `getMyLocations()` already restricts a supervisor's response
  // to their assigned locations, so the switcher inherits scoping for free.
  const {
    data: locationsData,
    isLoading: locationsLoading,
    isError: locationsError,
  } = useQuery({
    queryKey: ['my-locations'],
    queryFn: () => getMyLocations(),
  });

  const locations = locationsData?.locations ?? [];
  const locationNameById = useMemo(
    () => new Map(locations.map((loc) => [loc.id, loc.name])),
    [locations],
  );

  const locationTimeZoneById = useMemo(
    () => new Map(locations.map((loc) => [loc.id, loc.timezone])),
    [locations],
  );

  // R6: queryKey includes all filters — changing any filter resets pagination.
  const queryKey = useMemo(
    () => [
      'tickets',
      'browse',
      filters.locationId || null,
      filters.status || null,
      filters.paymentStatus || null,
      filters.from || null,
      filters.to || null,
    ],
    [filters],
  );

  const fetchPage = useMemo(
    () => (cursor: string | null) => {
      const params: TicketBrowseQuery = {
        cursor: cursor ?? undefined,
        limit: PAGE_LIMIT,
      };
      if (filters.locationId) params.locationId = filters.locationId;
      if (filters.status) params.status = filters.status;
      if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
      // Inclusive date-range window (mirrors AuditLogPage's from/to convention).
      if (filters.from) params.from = `${filters.from}T00:00:00.000Z`;
      if (filters.to) params.to = `${filters.to}T23:59:59.999Z`;
      return browseTickets(params).then((res) => ({
        items: res.items,
        nextCursor: res.nextCursor,
        // `TicketBrowseResponse` has no `hasMore` field — a non-null cursor
        // IS the "more pages exist" signal (mirrors the audit-log contract).
        hasMore: res.nextCursor !== null,
      }));
    },
    [filters],
  );

  const { items, hasMore, isLoading, isFetchingNextPage, fetchNextPage, error } =
    useCursorPagination({
      queryKey,
      fetchPage,
    });

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.browseTitle')}
        description={t('page.browseDescription')}
        actions={<TicketExportControl state={exportState} onExport={() => startExport(filters)} />}
      />

      {error && <ErrorBanner message={translateApiError(error, t)} />}

      {locationsError && (
        <p className="text-[12px] text-jq-danger">{t('errors.locationsLoadFailed')}</p>
      )}

      <TicketFilterBar
        filters={filters}
        onFiltersChange={setFilters}
        locations={locationsLoading ? [] : locations}
      />

      <TicketBrowseTable
        items={items}
        isLoading={isLoading}
        hasMore={hasMore}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={fetchNextPage}
        locationNameById={locationNameById}
        locationTimeZoneById={locationTimeZoneById}
      />
    </div>
  );
}

// tickets-csv-export (design D6) — "Export to CSV" control in the page
// header. Renders the appropriate affordance for each `TicketExportState`
// phase: an idle trigger button, an in-progress indicator (starting /
// pending / processing), a download link once `done`, or a failed state
// with a retry affordance that re-fires the export with the CURRENT filters.
function TicketExportControl({
  state,
  onExport,
}: {
  state: TicketExportState;
  onExport: () => void;
}) {
  const { t } = useTranslation('tickets');

  if (state.phase === 'idle') {
    return (
      <Button variant="outline" size="sm" onClick={onExport} className="text-[13px]">
        <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
        {t('export.toCsv')}
      </Button>
    );
  }

  if (state.phase === 'starting' || state.phase === 'pending' || state.phase === 'processing') {
    return (
      <Button variant="outline" size="sm" disabled className="text-[13px]">
        <RefreshCw className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} />
        {t('export.exporting')}
      </Button>
    );
  }

  if (state.phase === 'done') {
    return (
      <Button variant="accent" size="sm" asChild className="text-[13px]">
        <a href={state.downloadUrl} download>
          <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
          {t('export.downloadCsv')}
        </a>
      </Button>
    );
  }

  // failed | error
  // The job's raw `error` is a server exception text (S3/DB internals, English
  // only): never shown. Start failures arrive already translated.
  const message = state.phase === 'failed' ? t('export.jobFailed') : state.message;
  return (
    <div className="flex items-center gap-2">
      <p className="text-[12px] text-jq-danger" role="alert">
        {t('export.failed', { message })}
      </p>
      <Button variant="outline" size="sm" onClick={onExport} className="text-[13px]">
        <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />
        {t('export.retry')}
      </Button>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  const { t } = useTranslation('tickets');
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t('errors.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
